<!--赖传峰-->
<!--2025/2/20-->
<script lang="ts" setup>
import { authByLocalStorage, computed, inject, reactive, useDomain, useRoute, watch } from '#imports'
import $track from '@/track/webShare'
import axios from 'axios'
import { useI18n } from 'vue-i18n'
import { ReportTarget, ReportType, reportTypes } from '~/components/home/resolve-source/reportTypes'
import useMessage from '~/composables/useMessage'
import useShareId from '~/composables/useShareId'
import { useShare } from '~/store/share'
import { useUserInfo } from '~/store/userInfo'
import { EMAIL_REGEX } from '~/types/constants'
import { InjectionKey } from '~/types/enum'

const { t } = useI18n()
const domain = useDomain()
const shareStore = useShare()
const visible = defineModel<boolean>('visible')
const showLoginDialog = inject<ShowLoginDialog>(InjectionKey.SHOW_LOGIN_DIALOG)
const route = useRoute()
const shareId = useShareId()
const sharedUserId = computed<string>(() => shareStore.data.user_info?.user_id || '')
const selectedLength = computed<number>(() => shareStore.selectedIds.length)

const reportTargets = computed<{ value: ReportTarget; title: string }[]>(() => [
  {
    value: ReportTarget.SHARED,
    title: t('report-target-shared'),
  },
  {
    value: ReportTarget.FOLDER,
    title: t('report-target-folder'),
  },
  {
    value: ReportTarget.PARTIAL,
    title: selectedLength.value ? t('report-target-partial-count', [selectedLength.value]) : t('report-target-partial'),
  },
])
const initState = {
  target: ReportTarget.PARTIAL,
  type: undefined,
  email: undefined,
  emailValid: true,
  loading: false,
}
const state = reactive<{
  target: ReportTarget
  type?: ReportType
  email?: string
  emailValid?: boolean
  loading: boolean
}>({ ...initState })

watch(
  () => state.email,
  () => (state.emailValid = true),
)

const needSelectedFiles = computed(() => state.target === ReportTarget.PARTIAL && !selectedLength.value)

const btnDisabled = computed(
  () => !state.target || !state.type || !state.email || !state.emailValid || needSelectedFiles.value,
)

const reportData = () => {
  switch (state.target) {
    case ReportTarget.SHARED:
      return {
        resource: `${window.location.origin}/s/${route.params.share_id}`,
        sharedUserId: sharedUserId.value,
        fileIds: [],
      }
    case ReportTarget.FOLDER:
      return {
        resource: window.location.href,
        sharedUserId: sharedUserId.value,
        fileIds: [],
      }
    default:
      return {
        resource: window.location.href,
        sharedUserId: sharedUserId.value,
        fileIds: shareStore.selectedIds,
      }
  }
}

const report = async () => {
  if (state.email && !EMAIL_REGEX.test(state.email)) {
    state.emailValid = false
    return
  }

  if (!authByLocalStorage()) return showLoginDialog?.({ afterLogin: report, showHeaderText: false })
  const trackParams: Parameters<typeof $track.share_report_request>[number] = {
    share_id: shareId.value,
    share_userid: sharedUserId.value,
    report_reason: state.type!,
    report_target: state.target,
  }
  $track.share_report_request(trackParams)
  let result: Parameters<typeof $track.share_report_result>[number]['result'] = 'fail'
  try {
    state.loading = true
    const res = await axios.post(
      `https://api-feedback.${domain.value}/report`,
      {
        ...reportData(),
        reportType: state.type,
        userInfo: JSON.stringify({ from: 'share', ...(useUserInfo().$state || {}) }),
      },
      { headers: { 'Content-Type': 'application/json' } },
    )

    if (res.status === 200 && res.data.code === 0) {
      Object.assign(state, initState)
      visible.value = false
      result = 'success'
      useMessage({
        type: 'success',
        message: t('QFZIjVDfGWr3POVe1B'),
      })
    } else {
      throw Error('Response Error')
    }
  } catch {
    state.loading = false
    useMessage({
      type: 'error',
      message: t('failed-to-report'),
    })
  }
  $track.share_report_result({ result, ...trackParams })
}

watch(visible, val => {
  if (val) {
    // 埋点上报 分享举报弹窗曝光展示
    $track.share_report_pop_show({ share_id: shareId.value, share_userid: sharedUserId.value })
  } else {
    // 分享举报弹窗关闭、 数值初始化
    Object.assign(state, initState)
  }
})

const openReportDialog = (type?: ReportTarget) => {
  visible.value = true
  if (type === ReportTarget.SHARED) {
    // 初始化举报类型
    state.target = ReportTarget.SHARED
  }
}

defineExpose({
  openReportDialog,
})
</script>

<template>
  <ElDialog
    v-model="visible"
    :before-close="
      (done: VoidFunction) => {
        !state.loading && done()
      }
    "
    append-to-body
    class="report-dialog-shared"
    width="660"
  >
    <section>
      <h2 class="title">
        {{ $t('report-target') }}
      </h2>
      <div class="tip">
        {{ $t('report-target-tip') }}
      </div>
      <ul class="report-type">
        <li
          v-for="type in reportTargets"
          :key="type.value"
          @click="state.target = type.value"
        >
          <div class="report-type-item">
            <span
              :class="state.target === type.value && 'radio-checked'"
              class="pp-checkbox"
            />
            {{ type.title }}
          </div>
        </li>
      </ul>
    </section>
    <div
      v-if="needSelectedFiles"
      class="report-type-content"
    >
      {{ $t('require-file-ids') }}
    </div>
    <template v-if="!needSelectedFiles">
      <section>
        <h2 class="title mb-20">
          {{ $t('why-report') }}
        </h2>
        <ul class="report-type">
          <li
            v-for="type in reportTypes"
            :key="type.value"
            @click="state.type = type.value"
          >
            <div class="report-type-item">
              <span
                :class="state.type === type.value && 'radio-checked'"
                class="pp-checkbox"
              />
              {{ $t(type.title) }}
            </div>
            <div
              v-if="type.value === ReportType.HACKER && state.type === ReportType.HACKER"
              class="report-type-content-wrapper"
            >
              <div class="report-type-content">
                <i18n-t
                  keypath="report-hacker-content"
                  tag="span"
                >
                  <template #one>
                    <a href="mailto:abuse@mypikpak.com">abuse@mypikpak.com</a>
                  </template>
                </i18n-t>
              </div>
            </div>
          </li>
        </ul>
      </section>
      <section v-if="state.type !== ReportType.HACKER">
        <label
          for="email"
          style="color: var(--color-primary-text); display: block"
          class="required"
        >
          {{ $t('report-email') }}
        </label>
        <ElInput
          v-model="state.email"
          class="mail-input sp-input"
          :placeholder="$t('report-email-input-placeholder')"
          name="email"
          :formatter="(val: string) => val.trim()"
          style="margin-top: 8px"
        />
        <div
          v-if="!state.emailValid"
          style="transform: translate(0, 6px)"
          class="input-valid-message"
        >
          {{ $t('report-email-valid-msg') }}
        </div>
      </section>
    </template>

    <template #footer>
      <PPButton
        v-if="needSelectedFiles"
        @click="visible = false"
      >
        {{ $t('need-select-file-before-report') }}
      </PPButton>
      <PPButton
        v-else
        :class="{
          hidden: state.type === ReportType.HACKER,
        }"
        :disabled="btnDisabled"
        :loading="state.loading"
        height="36px"
        style="min-width: 120px"
        type="primary"
        class="btn"
        @click="report"
      >
        {{ $t('submit') }}
      </PPButton>
      <ElDivider />
      <div
        class="report-supplement"
        v-html="$t('report-supplement')"
      />
      <div
        class="report-supplement"
        style="margin-top: 10px"
        v-html="$t('report-supplement-organization')"
      />
    </template>
  </ElDialog>
</template>
<style scoped lang="less">
@import '@/assets/styles/screen.less';
.report-dialog-shared {
  -webkit-tap-highlight-color: transparent;
  section + section {
    margin-top: 32px;
  }

  .require-file-ids {
    color: var(--color-primary-text);
    font-size: 16px;
    padding-left: 32px;
  }

  .title {
    font-weight: 600;
    color: var(--color-primary-text);
    font-size: 18px;
  }

  .mb-20 {
    margin-bottom: 20px;
  }

  .tip {
    margin-top: 12px;
    margin-bottom: 20px;
    line-height: 20px;
    color: var(--color-error);
  }

  .report-type {
    margin-top: 6px;

    li {
      cursor: pointer;
      // height: 48px;
      line-height: 20px;
      padding: 0px 0;
      margin-bottom: 12px;
      &:last-child {
        margin-bottom: 0;
      }
      position: relative;

      .report-type-item {
        display: flex;
        align-items: center;
      }

      color: var(--color-primary-text);

      .pp-checkbox {
        margin-right: 16px;
      }

      // + li {
      //   border-top: 1px solid #f0f0f0;
      // }
    }
  }

  .report-type-content-wrapper {
    overflow: hidden;
    min-height: 104px;
  }

  .report-type-content {
    position: relative;
    margin: 12px 0 0 32px;
    padding: 10px 12px;
    display: flex;
    align-items: center;
    background-color: rgba(48, 110, 255, 0.08);
    border-radius: 8px;
    font-size: 13px;
    line-height: 18px;
    color: var(--color-primary-text);
    text-align: left;
    &:before {
      position: absolute;
      box-sizing: border-box;
      left: 30px;
      top: -4px;
      content: '';
      display: block;
      width: 8px;
      height: 8px;
      border-top: 4px solid rgba(48, 110, 255, 0.08);
      border-left: 4px solid rgba(48, 110, 255, 0.08);
      border-right: 4px solid transparent;
      border-bottom: 4px solid transparent;
      transform: rotate(45deg);
    }
  }

  .hidden {
    visibility: hidden;
  }

  .mail-input {
    width: 70%;
    .mobile({
      width: 100% !important;
    });
  }

  .btn {
    .mobile({
      width: 100% !important;
      height: 48px !important;
    });
  }

  .report-supplement {
    margin-top: 22px;
    width: 100%;
    text-align: left;
    color: var(--color-secondary-text);
    line-height: 20px;
  }
}
</style>

<style lang="less">
@import '@/assets/styles/screen.less';

.report-dialog-shared {
  -webkit-tap-highlight-color: transparent;

  .mobile({
    box-sizing: border-box;
    max-height: calc(var(--vh, 100vh) - 12px) !important;
  });

  .el-dialog__header {
    padding: 16px 0 0 !important;

    .el-dialog__headerbtn {
      top: 23px !important;
      right: 16px !important;
    }
  }

  .el-dialog__body {
    padding: 0 16px 4px !important;

    .mobile({
      padding: 0 0 0 !important;
    });
  }

  .el-dialog__footer {
    .mobile({
      padding-left: 0 !important;
      padding-right: 0 !important;
    });
  }

  .sp-input {
    --el-input-text-color: var(--color-primary-text);
    --el-input-bg-color: #f2f3f4;
    --el-input-border-color: transparent;
    --el-input-focus-border-color: var(--color-primary);
    .el-input__wrapper {
      height: 42px;
      border-radius: 4px;
    }
  }
}
</style>
